import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SlidersHorizontal, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CustomizeQuickActionsDialog } from "./CustomizeQuickActionsDialog";
import { useQuickActions } from "./use-quick-actions";

/**
 * The Workspace dashboard's top bar: one-click shortcuts to the forms this user
 * is allowed to submit, in the order they chose.
 *
 * Hidden entirely for a role that can submit nothing from the catalogue — a bar
 * whose only content is "you can't do anything here" is noise on every visit.
 * A user who *can* but unpinned everything still gets the bar, with a way back.
 */
export const QuickActionsBar = () => {
  const { t } = useTranslation(["company", "common"]);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const { available, pinned, isCustomized, isLoading, isSaving, save, reset } =
    useQuickActions();

  if (!isLoading && available.length === 0) return null;

  return (
    <section
      aria-label={t("company:dashboard_quick_actions.title")}
      className="widget-command-bar flex flex-col gap-2 rounded-sm px-3.5 py-2.5 md:flex-row md:items-center md:gap-4"
    >
      <div className="flex shrink-0 items-center gap-2">
        <span className="grid size-7 shrink-0 place-items-center rounded-sm bg-primary-muted text-primary">
          <Zap size={14} aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-foreground">
            {t("company:dashboard_quick_actions.title")}
          </h2>
          <p className="hidden truncate text-xs text-muted-foreground lg:block">
            {t("company:dashboard_quick_actions.subtitle")}
          </p>
        </div>
      </div>

      {/* Scrolls within itself on a narrow screen rather than giving the whole
          page a horizontal scrollbar. `min-w-0` is what makes it reachable. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ul className="scrollbar-hidden flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
          {isLoading
            ? Array.from({ length: 4 }, (_, index) => (
                <li key={index} className="shrink-0">
                  <Skeleton className="h-8 w-28 rounded-sm" />
                </li>
              ))
            : pinned.map((entry) => {
                const Icon = entry.icon;
                return (
                  <li key={entry.key} className="shrink-0">
                    <Button asChild variant="outline" size="sm">
                      <Link to={entry.to}>
                        <Icon size={14} aria-hidden className="text-primary" />
                        {t(entry.label)}
                      </Link>
                    </Button>
                  </li>
                );
              })}
          {!isLoading && pinned.length === 0 && (
            <li className="truncate text-xs text-muted-foreground">
              {t("company:dashboard_quick_actions.none_pinned")}
            </li>
          )}
        </ul>

        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          disabled={isLoading}
          onClick={() => setIsCustomizeOpen(true)}
          aria-label={t("company:dashboard_quick_actions.customize_with_count", {
            count: pinned.length,
            available: available.length,
          })}
        >
          <SlidersHorizontal size={14} aria-hidden />
          <span className="hidden sm:inline">
            {t("common:dashboard_grid.customize")}
          </span>
        </Button>
      </div>

      {isCustomizeOpen && (
        <CustomizeQuickActionsDialog
          open={isCustomizeOpen}
          onOpenChange={setIsCustomizeOpen}
          available={available}
          pinned={pinned}
          isCustomized={isCustomized}
          isSaving={isSaving}
          onSave={save}
          onReset={reset}
        />
      )}
    </section>
  );
};
